import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { usePost } from '../API';
import type { NotificationSummary, NotificationRecipientsResult } from '@aidkitorg/aidkit/lib/notifications/NotificationService';
import { logAdminPanelError } from '../utils/adminPanel.utils';

type NotificationsContextType = {
  notifications: Array<NotificationSummary>
  enabledNotifications: number,
  notificationsDataTimestamp?: number,

  isNotificationsLoading: boolean,
  getIsRecipientsLoading: (targetFieldPrefix: string) => boolean,
  
  fetchNotificationsOverview: (force?: boolean) => void,
  fetchNotificationRecipients: (targetFieldPrefix: string, force?: boolean) => Promise<void>,
  getNotificationRecipients: (targetFieldPrefix: string) => NotificationRecipientsResult | null,
  getRecipientsTimestamp: (targetFieldPrefix: string) => number | undefined
}

type NotificationsOverviewRef = {
  notifications?: Array<NotificationSummary>,
  timestamp?: number,
  expiresAt?: number,
}

type NotificationRecipientsRef = {
  data?: NotificationRecipientsResult,
  timestamp?: number,
  expiresAt?: number,
}

type NotificationsProviderProps = {
  children: React.ReactNode
}

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  enabledNotifications: 0,

  isNotificationsLoading: false,
  getIsRecipientsLoading: () => false,

  fetchNotificationsOverview: async (_force?: boolean) => {},
  fetchNotificationRecipients: async (_targetFieldPrefix: string, _force?: boolean) => {},
  getNotificationRecipients: () => null,
  getRecipientsTimestamp: () => undefined,
})

export const useNotifications = () => {
  const context = useContext(NotificationsContext)
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationsProvider")
  }

  return context;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Array<NotificationSummary>>([])
  const [enabledNotifications, setEnabledNotifications] = useState(0)
  const [notificationsDataTimestamp, setNotificationsDataTimestamp] = useState<number>()

  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
  const [isRecipientsLoading, setIsRecipientsLoading] = useState<{ [targetFieldPrefix: string]: boolean }>({})

  const notificationsRef = useRef<NotificationsOverviewRef>({})
  const notificationRecipientsRef = useRef<{ [targetFieldPrefix: string]: NotificationRecipientsRef }>({})

  const getNotificationsData = usePost("/api/admin_panel/notifications")
  const getNotificationRecipientsData = usePost("/api/admin_panel/notifications/recipients")

  const fetchNotificationsOverview = useCallback(async (force?: boolean) => {
    const now = Date.now()

    // Check if an unexpired value lives in the cache
    if (!force && 
      notificationsRef.current.notifications && 
      (notificationsRef.current.expiresAt && now < notificationsRef.current.expiresAt)) 
    {
      return
    }

    setIsNotificationsLoading(true)
    const result = await getNotificationsData({ force })
    setIsNotificationsLoading(false)

    if (result && result.success && result.data) {
      notificationsRef.current = { notifications: result.data.notifications, timestamp: result.data.timestamp, expiresAt: result.data.expiresAt }

      setEnabledNotifications(result.data.totalEnabledNotifications)
      setNotifications(result.data.notifications)
      setNotificationsDataTimestamp(result.data.timestamp)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [getNotificationsData])

  const fetchNotificationRecipients = useCallback(async (targetFieldPrefix: string, force?: boolean) => {
    const now = Date.now()

    // Check if an unexpired value lives in the cache
    if (!force && 
      notificationRecipientsRef.current[targetFieldPrefix]?.data && 
      (notificationRecipientsRef.current[targetFieldPrefix]?.expiresAt && now < notificationRecipientsRef.current[targetFieldPrefix].expiresAt!)) 
    {
      return
    }

    setIsRecipientsLoading(prev => ({ ...prev, [targetFieldPrefix]: true }))
    
    try {
      const result = await getNotificationRecipientsData({ targetFieldPrefix })

      if (result && result.success && result.data) {
        notificationRecipientsRef.current[targetFieldPrefix] = { 
          data: result.data, 
          timestamp: Date.now(), 
          expiresAt: Date.now() + 300000 // 5 minute cache
        }
      } else if (result.error) {
        logAdminPanelError(result.error, result.message)
      }
    } catch (error) {
      logAdminPanelError(error as any, 'Failed to fetch notification recipients')
    } finally {
      setIsRecipientsLoading(prev => ({ ...prev, [targetFieldPrefix]: false }))
    }
  }, [getNotificationRecipientsData])

  const getNotificationRecipients = useCallback((targetFieldPrefix: string): NotificationRecipientsResult | null => {
    return notificationRecipientsRef.current[targetFieldPrefix]?.data || null
  }, [])

  const getRecipientsTimestamp = useCallback((targetFieldPrefix: string): number | undefined => {
    return notificationRecipientsRef.current[targetFieldPrefix]?.timestamp
  }, [])

  const getIsRecipientsLoading = useCallback((targetFieldPrefix: string) => {
    return isRecipientsLoading[targetFieldPrefix] || false
  }, [isRecipientsLoading])


  const value = {
    notifications,
    enabledNotifications,
    notificationsDataTimestamp,

    isNotificationsLoading,
    getIsRecipientsLoading,

    fetchNotificationsOverview,
    fetchNotificationRecipients,
    getNotificationRecipients,
    getRecipientsTimestamp,
  }

  return (
    <NotificationsContext.Provider value={ value }>
      {children}
    </NotificationsContext.Provider>
  )
}
